import axios from "axios";
import { useFormik } from "formik";
import i18next from "i18next";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import React, { memo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import getEnv, { getGA } from "../../utils/getAppEnv";
import getBaseUrl from "../../utils/getBaseUrl";
import { getOutsideUrl } from "../../utils/getOutsideUrl";
import Loader from "../Loader/Loader";
import "./Email.css";

type Props = {
  label: string;
  details: {
    partNumberList: Array<any>;
    machinWeight?: string;
    rpmValue?: string;
    numOfMount?: string;
    failSafe?: boolean;
  };
  type: EMAIL_TYPE;
  hasSelectedMount?: boolean;
};

export const enum EMAIL_TYPE {
  "AVS_MOUNT_LIST" = 1,
  "AVS_MOUNT_DETAIL",
  "CATALOGUE_MOUNT_LIST",
  "CATALOGUE_MOUNT_DETAIL",
}

const Email: React.FC<Props> = ({ label, details, type, hasSelectedMount }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleEmailClick = () => {
    setVisible(true);
    (window as any).dataLayer.push({
      event: "engagement",
      eventCategory: "Mountfinder Category",
      eventAction: "Click", // 'Weight of the Machine', 'Machine Speed', 'Number of Mounts' and 'Category'
      eventLabel: "Email result",
    });
  };

  const toast = useRef<any>();

  const defaultValues: any = {
    name: "",
    pemail: "",
    semail: "",
    notes: "",
    addInfo: false,
    subscribe: false,
  };
  const formik = useFormik({
    initialValues: defaultValues,
    validate: (data) => {
      let errors: any = {};

      if (!data.name || !data.name.trim()) {
        errors.name = "NAME_REQ";
      }
      if (data.name.trim().length >= 75) {
        errors.name = "MAX_CHAR_75";
      }
      if (data.pemail.trim().length >= 75) {
        errors.pemail = "MAX_CHAR_75";
      }
      if (!data.pemail) {
        errors.pemail = "EMAIL_REQ";
      } else if (
        !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
          data.pemail
        )
      ) {
        errors.pemail = "WRONG_MAIL";
      }

      if (data.semail.trim().length >= 75) {
        errors.semail = "MAX_CHAR_75";
      }
      try {
        if (
          data.semail &&
          !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
            data.semail
          )
        ) {
          errors.semail = "WRONG_MAIL";
        } else if (data.semail && data.pemail === data.semail) {
          errors.semail = "DUPLICATE_MAIL";
        }
        if (data.notes.trim().length >= 2000) {
          errors.notes = "MAX_CHAR_200";
        }
      } catch (err) {
        console.log(err);
      }
      return errors;
    },
    onSubmit: async (data) => {
      try {
        setLoading(true);

        if (
          type === EMAIL_TYPE.AVS_MOUNT_DETAIL ||
          type === EMAIL_TYPE.AVS_MOUNT_LIST
        ) {
          await axios.post(
            `${getBaseUrl()}productcontroller/sendAVSMountReport`,
            {
              deviceType: 1,
              languageCode: i18next.language,
              audioValue: 0,
              disturbingFrequency: 0,
              name: data.name,
              primaryMail: data.pemail,
              secondaryMail: data.semail,
              failSafeValue: details.failSafe ? true : false,
              mailNote: data.notes,
              salesManagerMailStatus: data.addInfo,
              newsletterSignupStatus: data.subscribe,
              partNumberList: details.partNumberList,
              machinWeight: Number(details.machinWeight) || 0,
              rpmValue: Number(details.rpmValue) || 0,
              numOfMount: Number(details.numOfMount) || 0,
              mailType: type,
            }
          );
        } else {
          await axios.post(
            `${getBaseUrl()}catalogueController/sendCatalogueMountReport`,
            {
              name: data.name,
              primaryMail: data.pemail,
              secondaryMail: data.semail,
              mailNote: data.notes,
              mailType: type,
              partNumberList: details.partNumberList,
              failSafeValue: details.failSafe ? true : false,
              salesManagerMailStatus: data.addInfo,
              newsletterSignupStatus: data.subscribe,
              languageCode: i18next.language,
            }
          );
        }
        const resp = await axios.get("https://jsonip.com");
        let ip = undefined;
        if (resp.data && resp.data.ip) {
          ip = resp.data.ip.split(",")[0];
        }
        axios.post(
          `https://api.hsforms.com/submissions/v3/integration/submit/24909964/e0ab71a1-155c-4c40-a951-46ac06831be1`,
          {
            submittedAt: Date.now(),
            fields: [
              {
                objectTypeId: "0-1",
                name: "firstname",
                value: data.name,
              },
              {
                objectTypeId: "0-1",
                name: "email",
                value: data.pemail,
              },
              {
                objectTypeId: "0-1",
                name: "secondary_email",
                value: data.semail,
              },
              {
                objectTypeId: "0-1",
                name: "message",
                value: data.notes,
              },
            ],
            legalConsentOptions: {
              consent: {
                consentToProcess: true,
                text: `By clicking on Send Results, you give consent for Trelleborg Antivibration Solutions to store and process your personal information on the legal basis of legitimate interest, to provide you with the relevant information you have requested. You can unsubscribe from the communications at any time. For more information check our Privacy Notice`,
                communications: [
                  {
                    value: data.subscribe,
                    subscriptionTypeId: 117580087,
                    text: "I agree to receive communications from Trelleborg Antivibration Solutions.",
                  },
                  {
                    value: data.addInfo,
                    subscriptionTypeId: 134816783,
                    text: "Send a copy of the results to Trelleborg to request further information.",
                  },
                ],
              },
            },
            context: {
              pageUri: window.location.href,
              pageName: window.location.hash.replace("#/", ""),
              ipAddress: ip,
            },
          }
        );

        // (window as any).hbspt.forms.create({
        //   region: "eu1",
        //   portalId: "24909964",
        //   formId: "e0ab71a1-155c-4c40-a951-46ac06831be1",
        // });

        formik.resetForm();
        setLoading(false);
        if (toast && toast.current && toast.current.show) {
          toast.current.show({
            severity: "success",
            summary: t("SUC"),
            detail: t("EMAIL_SUC"),
          });
        }
        setVisible(false);
      } catch (err) {
        setLoading(false);

        if (toast && toast.current && toast.current.show) {
          toast.current.show({
            severity: "error",
            summary: t("WARN"),
            detail: t("SOM"),
          });
        }
      }
    },
  });
  const isFormFieldValid = (name: string) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: string) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{t(formik.errors[name] as string)}</small>
      )
    );
  };

  return (
    <>
      {loading ? <Loader /> : ""}
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        header={t(label)}
        onHide={() => {
          setVisible(false);
          formik.resetForm();
        }}
        resizable={false}
        style={{ width: "50vw" }}
        breakpoints={{ "1020px": "90vw" }}
        draggable={false}
        blockScroll={true}
        className="email-ffff"
      >
        <form
          className="email-form"
          noValidate={true}
          onSubmit={formik.handleSubmit}
        >
          <label className="block mb-2 text-xs" htmlFor="name">
            {t("NAME")} <span className="required">*</span>
          </label>
          <input
            className={`block ${isFormFieldValid("name") ? "error-input" : ""}`}
            type="text"
            id="name"
            name="name"
            placeholder={t("YOUR_NAME")}
            autoComplete="off"
            value={formik.values.name}
            onChange={formik.handleChange}
          />
          {isFormFieldValid("name") ? (
            <span className="error">{getFormErrorMessage("name")}</span>
          ) : (
            ""
          )}
          <label className="block mb-2 mt-4 text-xs" htmlFor="pemail">
            {t("EMAIL")} <span className="required">*</span>
          </label>
          <input
            type="email"
            className={`block ${
              isFormFieldValid("pemail") ? "error-input" : ""
            }`}
            id="pemail"
            name="pemail"
            autoComplete="off"
            placeholder={t("YOU_EMAIL")}
            value={formik.values.pemail}
            onChange={formik.handleChange}
          />
          {isFormFieldValid("pemail") ? (
            <span className="error">{getFormErrorMessage("pemail")}</span>
          ) : (
            ""
          )}
          <label className="block mb-2 mt-4 text-xs" htmlFor="semail">
            {t("SEC_EMAIL")}
          </label>
          <input
            className={`block  ${
              isFormFieldValid("semail") ? "error-input" : ""
            }`}
            type="email"
            id="semail"
            name="semail"
            autoComplete="off"
            placeholder={t("SEC_EMAIL_PLAC")}
            value={formik.values.semail}
            onChange={formik.handleChange}
          />
          {isFormFieldValid("semail") ? (
            <span className="error">{getFormErrorMessage("semail")}</span>
          ) : (
            ""
          )}
          <label className="block mb-2 mt-4 text-xs" htmlFor="notes">
            {t("NOTES")}
          </label>
          <textarea
            placeholder=""
            name="notes"
            className={`block  ${
              isFormFieldValid("notes") ? "error-input" : ""
            }`}
            id="notes"
            cols={30}
            rows={5}
            value={formik.values.notes}
            onChange={formik.handleChange}
          ></textarea>
          {isFormFieldValid("notes") ? (
            <span className="error">{getFormErrorMessage("notes")}</span>
          ) : (
            ""
          )}

          <div className="grid my-4">
            <div className="pb-0 col-12">
              <label htmlFor="addInfo" className="flex pb-3">
                <Checkbox
                  className="block email-cbx mr-2"
                  name="addInfo"
                  id="addInfo"
                  onChange={formik.handleChange}
                  checked={formik.values.addInfo}
                />
                <div className="helper-text">{t("ADDITIONAL_INFO")}</div>
              </label>
              <label htmlFor="subscribe" className="flex">
                <Checkbox
                  className="block email-cbx mr-2"
                  name="subscribe"
                  id="subscribe"
                  onChange={formik.handleChange}
                  checked={formik.values.subscribe}
                />
                <div className="helper-text">{t("SUBSCRIBE")}</div>
              </label>
              <div className="grid">
                <p className="font-primary col-12 font-12 line-height-3 mt-3">
                  <Trans
                    i18nKey="LINK_RES"
                    components={[
                      <a
                        className="link"
                        href={getOutsideUrl("privacy")}
                        title={t("PRIV_NO")}
                        target={"_blank"}
                      ></a>,
                    ]}
                  ></Trans>
                </p>
                <div className="flex col-12 md:col-4 justify-content-end align-items-end"></div>
              </div>
              <div className="grid">
                <p className="font-primary col-12 md:col-8 font-12 line-height-3 mt-3 pb-0"></p>
                <div className="flex col-12 md:col-4 justify-content-end align-items-end pb-0">
                  <Button className="email-btn" type="submit">
                    {t(label)}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Dialog>
      <Button
        disabled={hasSelectedMount}
        onClick={handleEmailClick}
        className="email-btn"
      >
        {t(label)}
      </Button>
      {/* <Dialog
        visible={gvisible}
        header={t("SEND_EMAIL")}
        style={{ width: "30vw" }}
        resizable={false}
        breakpoints={{ "960px": "50vw", "640px": "90vw" }}
        draggable={false}
        blockScroll={true}
        onHide={() => {
          setGVisible(false);
        }}
      >
        <div className="line-height-4">
          <Trans
            i18nKey="GDPR_INFO"
            components={[
              <a
                className="link"
                href={getOutsideUrl("privacy")}
                title={t("PRIV_NO")}
                target={"_blank"}
              ></a>,
            ]}
          ></Trans>
        </div>
        <div className="flex justify-content-end mt-4 ">
          <Button
            className="email-btn"
            onClick={() => {
              setGVisible(false);
              setVisible(true);
            }}
            label={t("OK")}
          />
        </div>
      </Dialog> */}
    </>
  );
};

export default memo(Email);
